import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface UseProductProps {
  productId: string;
  variantId?: string;
}

interface Store {
  id: string;
  store_name: string;
  description: string | null;
  suburb: string | null;
  state: string | null;
  florist_id: string;
  coordinates: { lat: number; lng: number } | null;
  delivery_radius: number;
  delivery_days: string[];
  delivery_fee: number;
  minimum_order_amount: number;
  same_day_enabled: boolean;
  delivery_cutoff_times: Record<string, string>;
  store_status: string;
  approval_status: string;
  storefront_image_url?: string | null;
  cover_image_url?: string | null;
}

interface ProductSize {
  id: string;
  size_name: string;
  price: number;
  main_image_url: string | null;
  gallery_image_urls: string[] | null;
  use_main_product_image: boolean;
  is_active: boolean;
}

interface ProductData {
  id: string;
  title: string;
  description: string | null;
  price: number;
  main_image_url: string | null;
  gallery_image_urls: string[] | null;
  category: string | null;
  active: boolean;
  created_at: string;
  updated_at: string;
  store_id: string;
  store: Store;
  sizes: ProductSize[];
}

interface Variant {
  id: string;
  size: string;
  price: number;
  main_image_url: string | null;
  is_active: boolean;
}

interface Product {
  id: string;
  uniqueId: string;
  title: string;
  description: string | null;
  category: string | null;
  main_image_url: string | null;
  gallery_image_urls: string[];
  store: Store;
  store_id: string;
  variants: Variant[];
  selectedVariant?: Variant | null;
}

/**
 * Utility function to add timeout to a promise
 * @param promise The promise to add timeout to
 * @param timeoutMs Timeout in milliseconds
 * @param errorMessage Custom error message
 */
function withTimeout<T>(promise: Promise<T>, timeoutMs: number, errorMessage: string): Promise<T> {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) => {
      setTimeout(() => {
        reject(new Error(errorMessage));
      }, timeoutMs);
    })
  ]);
}

/**
 * Hook to fetch product details including variants and store information
 * @param productId - The ID of the product to fetch
 * @param variantId - The ID of the variant to fetch
 * @returns Query result containing product data, loading state, and error if any
 */
export function useProduct({ productId, variantId }: UseProductProps) {
  return useQuery<Product>({
    queryKey: ["product", productId, variantId],
    queryFn: async () => {
      try {
        // Validate input
        if (!productId?.trim()) {
          console.error("useProduct - Product ID is empty or invalid");
          throw new Error("Product ID is required");
        }

        console.log('useProduct - Fetching product:', productId, 'variant:', variantId);

        // Fetch product with store data with a timeout
        const fetchPromise = supabase
          .from("products")
          .select(`
            id,
            title,
            description,
            price,
            main_image_url,
            gallery_image_urls,
            category,
            active,
            created_at,
            updated_at,
            store_id,
            store:store_profiles(
              id,
              store_name,
              description,
              suburb,
              state,
              florist_id,
              coordinates,
              delivery_radius,
              delivery_days,
              delivery_fee,
              minimum_order_amount,
              same_day_enabled,
              delivery_cutoff_times,
              store_status,
              approval_status,
              storefront_image_url,
              cover_image_url
            ),
            sizes:product_sizes(
              id,
              size_name,
              price,
              main_image_url,
              gallery_image_urls,
              use_main_product_image,
              is_active
            )
          `)
          .eq("id", productId)
          .single();
          
        // Add a 10-second timeout to the query
        const { data: productData, error: productError } = await withTimeout(
          fetchPromise,
          10000, // 10 seconds timeout
          "Product fetch timed out after 10 seconds"
        );

        if (productError) {
          console.error("useProduct - Error fetching product:", productError);
          console.error("useProduct - Query params:", { productId, variantId });
          throw new Error(`Failed to fetch product: ${productError.message}`);
        }

        if (!productData) {
          console.error("useProduct - No product found for ID:", productId);
          throw new Error(`Product not found with ID: ${productId}`);
        }

        const data = productData as unknown as ProductData;
        console.log('useProduct - Raw product data:', JSON.stringify(data, null, 2));
        
        // Check if store exists and is active/approved
        if (!data.store) {
          console.error("useProduct - Product has no associated store:", productId);
          throw new Error("Product has no associated store");
        }
        
        if (data.store.store_status !== 'active' || data.store.approval_status !== 'approved') {
          console.error("useProduct - Store is not active or not approved:", {
            storeId: data.store.id,
            status: data.store.store_status,
            approval: data.store.approval_status
          });
          throw new Error("Store is not active or not approved");
        }

        // Transform variants
        const transformedVariants = (data.sizes || [])
          .filter(variant => variant.is_active)
          .map(variant => ({
            id: variant.id,
            size: variant.size_name,
            price: variant.price,
            main_image_url: variant.main_image_url,
            is_active: variant.is_active
          }));
          
        console.log('useProduct - Transformed variants:', transformedVariants);

        // Find selected variant
        let selectedVariant = null;
        if (variantId) {
          selectedVariant = transformedVariants.find(v => v.id === variantId);
          if (!selectedVariant) {
            console.warn(`useProduct - Specified variant ID ${variantId} not found in product variants`);
          }
        }
        // If no variant specified or not found, use first active variant
        if (!selectedVariant && transformedVariants.length > 0) {
          selectedVariant = transformedVariants[0];
          console.log('useProduct - Using default variant:', selectedVariant.id);
        }

        // Create unique ID for product + variant combination
        const uniqueId = selectedVariant 
          ? `${data.id}-${selectedVariant.id}`
          : `${data.id}-default`;

        // Transform and return the data
        const result = {
          id: data.id,
          uniqueId,
          title: data.title || "Untitled Product",
          description: data.description,
          category: data.category,
          main_image_url: data.main_image_url,
          gallery_image_urls: data.gallery_image_urls || [],
          store: data.store,
          store_id: data.store_id,
          variants: transformedVariants,
          selectedVariant
        };
        
        console.log('useProduct - Returning product data:', {
          id: result.id,
          title: result.title,
          variantsCount: result.variants.length,
          selectedVariant: result.selectedVariant?.id
        });
        
        return result;
      } catch (error) {
        console.error('useProduct - Unhandled error:', error);
        throw error;
      }
    },
    enabled: !!productId,
    gcTime: 1000 * 60 * 30, // Keep in cache for 30 minutes
    staleTime: 1000 * 60 * 5, // Consider data fresh for 5 minutes
    retry: (failureCount, error) => {
      // Don't retry if product not found, store issues, or timeout
      if (
        error instanceof Error && 
        (error.message.includes('not found') || 
         error.message.includes('no associated store') ||
         error.message.includes('not active or not approved') ||
         error.message.includes('timed out'))
      ) {
        console.log('useProduct - Not retrying query due to error type:', error.message);
        return false;
      }
      return failureCount < 2; // retry twice for other errors
    }
  });
} 